/**
 * Staff list view — designer OrdersTable (Downloads/psg/react/OrdersTable.jsx)
 * wired for staff Orders, Quotes, and RFQs.
 * Visual markup and tokens stay with the designer file.
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { formatEastern } from 'app/main/apps/common/easternTime';
import { Link } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import OrderViewAction from 'app/main/apps/common/OrderViewAction';
import { Loader } from 'app/main/apps/common/ListInfoMessage';
import MuiButton from '@mui/material/Button';
import MuiTooltip from '@mui/material/Tooltip';
import ProgressCell from 'app/main/apps/e-commerce/orders/ProgressCell';
import { displayName, formatWeight } from 'app/main/apps/common/AuraFunctions';
import { VAS_TYPE_CHOICES, shouldShowOrderVasTypes, vasTypeFilterLabels } from 'app/main/apps/e-commerce/shared/vasFilterChoices';
import OrdersToolBar from 'app/main/apps/e-commerce/orders/OrdersToolBar';
import StaffListRowActions from './StaffListRowActions';
import { TypeField, PeopleField, ReorderDateField } from './StaffListFilters';
import { StaffListColFilter, STAFF_LIST_COL_FILTER_PARAM_KEYS, STAFF_LIST_SORT_KEYS, staffListSortParamKey } from './StaffListColFilters';
import {
  hideStaffListRowActions,
  mergeStaffListCols,
  readStaffListLayout,
  staffListActionsColStyle,
  staffListColFiltersOpen,
  staffListColumnWidthStyle,
  staffListDateLabel,
  staffListDefaultCols,
  staffListEtaLabel,
  staffListIsContainer,
  staffListPinnedActionsColWidth,
  staffListRecordNoLabel,
  staffListResetClearsCount,
  STAFF_LIST_ACTIONS_COL_WIDTH,
  STAFF_LIST_CONTAINER_ONLY_COLUMN_KEYS,
  staffListShowsAmount,
  staffListShowsContainer,
  staffListShowsDue,
  staffListShowsPaid,
  staffListShowsPaymentType,
  staffListShowsDueDate,
  staffListShowsInternalNotesIcon,
  STAFF_LIST_INTERNAL_NOTES_LABEL,
  staffListShowsShippedDate,
  staffListShowsCtn,
  staffListShowsTax,
  staffListPaidLabel,
  STAFF_LIST_WRAPPING_HEADER,
  staffListTaxLabel,
  staffListShowsQuoteCounts,
  staffListShowsWeight,
  staffListShowsStatusDays,
  staffListShowsOrderOnlyColumn,
  staffListResolvedColumn,
  staffListTableMinWidth,
  staffListPinsColumns,
  staffListPinnedColumn,
  STAFF_LIST_PIN_CLASS,
  STAFF_LIST_CHECKBOX_COL_WIDTH,
  staffStatusTone,
  staffListScopeLabel,
  writeStaffListLayout,
} from './staffListUi';
import { STAFF_LIST_TEST_IDS } from '../staffListTestIds';

/* ---------------------------------------------------------------- tokens */

const T = {
  blue: '#0f4fd8',
  blueDark: '#0b3fb0',
  blueTint: '#eaf0ff',
  blueTintBorder: '#b9cdfb',
  blueText: '#1a44a8',
  rowSelected: '#f3f7ff',
  ink: '#111821',
  ink2: '#28323f',
  muted: '#5f6b7a',
  muted2: '#6d7887',
  label: '#7b8695',
  faint: '#8c97a6',
  faint2: '#a4adba',
  icon: '#667384',
  // PGSS-5759: the internal-notes icon beside the number. Teal: apart from the blue number, the red VAS brush, the
  // purple revision-request number and the red overdue number.
  internalNote: '#0f766e',
  line: '#e3e8ef',
  line2: '#eceff3',
  line3: '#f1f3f6',
  field: '#d5dbe4',
  page: '#f4f6f8',
  sans: "'IBM Plex Sans', system-ui, sans-serif",
  mono: "'IBM Plex Mono', ui-monospace, monospace",
};

const STATUS = {
  'Ready For Shipment':    { bg: '#e7f5ec', fg: '#0f6b3d', border: '#c6e8d3', dot: '#1a9457' },
  'Order Ready Announced': { bg: '#eaf0ff', fg: '#1a44a8', border: '#d3e0ff', dot: '#2f6bf0' },
  'VAS Revision Required': { bg: '#fff3e6', fg: '#9a4b06', border: '#fde0c0', dot: '#ef8b1c' },
};

export const COLUMNS = [
  { key: 'orderNo',   label: 'Order no',     width: 112, align: 'left'   },
  { key: 'customer',  label: 'Customer',     width: 128, align: 'left'   },
  { key: 'customerName', label: 'Customer',  width: 128, align: 'left'   },
  { key: 'orderDate', label: 'Order date',   width: 108, align: 'left'   },
  { key: 'dueDate',   label: 'Due date',     width: 108, align: 'left'   },
  { key: 'shippedDate', label: 'Shipped date', width: 116, align: 'left' },
  { key: 'ref',       label: 'Reference no', width: 156, align: 'left'   },
  { key: 'decedent',  label: 'Decedent',     width: 168, align: 'left'   },
  { key: 'comments',  label: 'Notes',        width: 160, align: 'left'   },
  { key: 'eta',       label: 'Delivery ETA', width: 116, align: 'left'   },
  { key: 'weight',    label: displayName?.weight, width: 104, align: 'right' },
  { key: 'qty',       label: 'Qty',          width: 56,  align: 'right'  },
  { key: 'amount',    label: 'Amount',       width: 116, align: 'right'  },
  { key: 'tax',       label: displayName?.tax || 'Tax', width: 120, align: 'right' },
  { key: 'paid',      label: 'Paid',         width: 120, align: 'right'  },
  { key: 'due',       label: 'Due',          width: 100, align: 'right'  },
  { key: 'paymentType', label: 'Payment type', width: 110, align: 'left' },
  { key: 'type',      label: 'Order type',   width: 64,  align: 'center' },
  { key: 'container', label: 'Container no', width: 110, align: 'left'   },
  { key: 'ctn',       label: 'CTN',          width: 120, align: 'left'   },
  { key: 'status',    label: 'Status',       width: 176, align: 'left'   },
  { key: 'statusDays', label: 'Days in status', width: 112, align: 'right' },
  { key: 'quoteCounts', label: 'Quote Sent/Received', width: 140, align: 'left' },
  { key: 'shipmentDate', label: 'Shipment Date', width: 120, align: 'left' },
  { key: 'capacity',  label: 'Capacity',     width: 190, align: 'left'   },
  { key: 'noOfOrders', label: 'No of Orders', width: 100, align: 'left'   },
  { key: 'location',  label: 'Location',     width: 100, align: 'left'   },
];

export const DEFAULT_HIDDEN_COLUMNS = ['comments', 'type', 'container', 'decedent', 'statusDays'];

const SCOPE_CHIP_KEYS = ['invoiced', 'archived', 'vas', 'actionable', 'cancelled'];
const SCOPE_TEST_IDS = {
  invoiced: STAFF_LIST_TEST_IDS.scopeInvoiced,
  archived: STAFF_LIST_TEST_IDS.scopeArchived,
  vas: STAFF_LIST_TEST_IDS.scopeVas,
  actionable: STAFF_LIST_TEST_IDS.scopeActionable,
  cancelled: STAFF_LIST_TEST_IDS.scopeCancelled,
};

export const sampleOrders = [
  { id: '26343', qty: 2, amount: 2377.24, status: 'Ready For Shipment' },
  { id: '26342', qty: 1, amount: 1652.62, status: 'Order Ready Announced' },
  { id: '26341', qty: 1, amount: 1238.67, status: 'Ready For Shipment' },
  { id: '26340', qty: 2, amount: 2596.61, status: 'Ready For Shipment' },
  { id: '26339', qty: 2, amount: 2243.41, status: 'Ready For Shipment' },
  { id: '26338', qty: 1, amount: 1457.29, status: 'Order Ready Announced' },
  { id: '26337', qty: 2, amount: 2060.41, status: 'Ready For Shipment' },
  { id: '26336', qty: 1, amount: 1419.55, status: 'Order Ready Announced' },
  { id: '26335', qty: 2, amount: 2393.50, status: 'VAS Revision Required' },
  { id: '26333', qty: 2, amount: 2213.21, status: 'Order Ready Announced' },
].map(r => ({
  ...r,
  customer: 'MMash@Co',
  orderDate: 'Aug 21, 2026',
  ref: 'Auto-VAS PO-' + r.id,
  decedent: 'Auto-VAS Production ' + r.id,
  eta: 'Dec 18, 2026',
  type: 'R',
  container: '—',
}));

const money = n => '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/* ------------------------------------------------------------- primitives */

/** Inline-style hover without a stylesheet: spread {...h.bind} and use h.style. */
function useHover(base, hover) {
  const [on, setOn] = useState(false);
  return {
    style: on ? { ...base, ...hover } : base,
    bind: { onMouseEnter: () => setOn(true), onMouseLeave: () => setOn(false) },
  };
}

function Icon({ d, size = 15, width = 1.8, fill = 'none', style }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke="currentColor"
      strokeWidth={width} strokeLinecap="round" strokeLinejoin="round" style={style}>
      {d.map((p, i) => <path key={i} d={p} />)}
    </svg>
  );
}

const P = {
  filter: ['M3 5h18', 'M6 12h12', 'M10 19h4'],
  filterSm: ['M3 6h18', 'M7 12h10', 'M10 18h4'],
  chevron: ['m6 9 6 6 6-6'],
  upload: ['M12 16V4', 'm7 9 5-5 5 5', 'M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2'],
  download: ['M12 4v12', 'm7 11 5 5 5-5', 'M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2'],
  plus: ['M12 5v14', 'M5 12h14'],
  pencil: ['M12 20h9', 'M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z'],
  comment: ['M21 15a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z'],
  eye: ['M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7z', 'M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0z'],
  dollar: ['M12 2v20', 'M17 6.5c0-1.9-2.2-3-5-3s-5 1.1-5 3 2.2 3 5 3.5 5 1.6 5 3.5-2.2 3-5 3-5-1.1-5-3'],
  dots: ['M6.4 12a1.4 1.4 0 1 1-2.8 0 1.4 1.4 0 0 1 2.8 0z', 'M13.4 12a1.4 1.4 0 1 1-2.8 0 1.4 1.4 0 0 1 2.8 0z', 'M20.4 12a1.4 1.4 0 1 1-2.8 0 1.4 1.4 0 0 1 2.8 0z'],
  columns: ['M5 4h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z', 'M9 4v16', 'M15 4v16'],
  excel: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6', 'm9 13 6 5', 'm15 13-6 5'],
  csv: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6', 'M8 13h8', 'M8 17h5'],
  pdf: ['M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z', 'M15 2v5h5', 'M9 16h6'],
  pdf2: ['M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z', 'M15 2v5h5', 'M9 13h6', 'M9 17h4'],
  archive: ['M5 4h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z', 'M3 9h18', 'm9 14 3 3 3-3'],
  mail: ['M4 5h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z', 'm2 7 10 6 10-6'],
  lines: ['M4 6h16', 'M4 12h16', 'M4 18h10'],
  cancel: ['M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z', 'm15 9-6 6', 'm9 9 6 6'],
  refresh: ['M21 12a9 9 0 1 1-2.64-6.36', 'M21 4v5h-5'],
  location: ['M12 21s-7-6.2-7-11a7 7 0 1 1 14 0c0 4.8-7 11-7 11z', 'M14.5 10a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z'],
  internalNote: ['M15 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9z', 'M15 3v6h6', 'M7 13h6', 'M7 17h9'],
};

const CARET = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='%238c97a6' stroke-width='2' stroke-linecap='round'><path d='m3 4.5 3 3 3-3'/></svg>";

const selectStyle = (pad = 30) => ({
  height: 34,
  padding: `0 ${pad}px 0 10px`,
  background: `#fff url("${CARET}") no-repeat right ${pad - 20}px center`,
  border: `1px solid ${T.field}`,
  borderRadius: 7,
  fontSize: 13,
  color: T.muted,
  appearance: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
});

const eyebrow = {
  fontSize: 10.5, fontWeight: 600, letterSpacing: '0.06em',
  textTransform: 'uppercase', color: T.label,
};

// PGSS-5556: the table is tableLayout:'fixed', so a header wider than its column does not widen
// it — it spills over the next header. Cells have always clipped (see `clip` below); headers never
// did, which is why long configured labels overlapped. Clip here too, and sit wrapped headers on
// the baseline of the single-line ones beside them.
const thBase = {
  padding: '9px 14px', textAlign: 'left', fontSize: 10.5, fontWeight: 600,
  letterSpacing: '0.06em', textTransform: 'uppercase', color: T.muted2,
  borderBottom: `1px solid ${T.line}`,
  overflow: 'hidden', verticalAlign: 'bottom',
};

const clip = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

const checkbox = { width: 15, height: 15, accentColor: T.blue, cursor: 'pointer', margin: 0 };

function Button({ variant = 'default', size = 'md', active, children, style, ...rest }) {
  const h = size === 'sm' ? 30 : 34;
  const base = {
    display: 'flex', alignItems: 'center', gap: 7, flex: '0 0 auto', whiteSpace: 'nowrap',
    height: h, padding: size === 'sm' ? '0 11px' : '0 12px',
    borderRadius: size === 'sm' ? 6 : 7,
    fontSize: size === 'sm' ? 12.5 : 13, fontWeight: 500, cursor: 'pointer',
    fontFamily: 'inherit', background: '#fff', border: `1px solid ${T.field}`, color: T.ink2,
    ...style,
  };
  let hover = { background: '#f7f9fc', borderColor: '#b9c2cf' };
  if (variant === 'primary') {
    Object.assign(base, {
      background: T.blue, borderColor: T.blue, color: '#fff', fontWeight: 600,
      padding: size === 'sm' ? '0 11px' : '0 14px',
      boxShadow: size === 'sm' ? 'none' : '0 1px 2px rgba(15,79,216,.28)',
    });
    hover = { background: T.blueDark, borderColor: T.blueDark };
  }
  if (variant === 'ghost') {
    Object.assign(base, { background: 'transparent', border: 0, color: T.muted, fontWeight: 400 });
    hover = { color: T.ink2 };
  }
  if (active) Object.assign(base, { background: T.blueTint, borderColor: T.blueTintBorder, color: T.blueText });
  const hv = useHover(base, hover);
  return <button type="button" {...hv.bind} style={hv.style} {...rest}>{children}</button>;
}

function MenuItem({ icon, color, children, onClick, href, to, download, title }) {
  const h = useHover(
    { display: 'flex', width: '100%', alignItems: 'center', gap: 9, padding: '8px 10px',
      background: 'transparent', border: 0, borderRadius: 6, fontSize: 13, color: T.ink2,
      cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', textDecoration: 'none',
      boxSizing: 'border-box' },
    { background: T.page }
  );
  const inner = (
    <>
      <Icon d={icon} width={1.7} style={{ color, flex: '0 0 auto' }} />
      {children}
    </>
  );
  if (to) {
    return <Link to={to} title={title} {...h.bind} style={h.style}>{inner}</Link>;
  }
  if (href) {
    return <a href={href} download={download} title={title} {...h.bind} style={h.style}>{inner}</a>;
  }
  return (
    <button type="button" title={title} onClick={onClick} {...h.bind} style={h.style}>
      {inner}
    </button>
  );
}

function PageButton({ disabled, onClick, children, ...rest }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      {...rest}
      style={{
        height: 30, padding: '0 11px',
        background: disabled ? '#fbfcfd' : '#fff',
        border: `1px solid ${T.line}`, borderRadius: 6, fontSize: 12.5,
        color: disabled ? '#b0b9c5' : T.ink2,
        cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
      }}
    >
      {children}
    </button>
  );
}

function IconButton({ icon, title, onClick, active }) {
  const h = useHover(
    { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32,
      padding: 0, background: active ? '#e2e8f0' : 'transparent', border: 0, borderRadius: 7,
      color: active ? '#1e2936' : T.icon, cursor: 'pointer' },
    { background: '#e9edf3', color: '#1e2936' }
  );
  return (
    <button type="button" title={title} aria-label={title} onClick={onClick} {...h.bind} style={h.style}>
      <Icon d={icon} size={17} width={1.7} />
    </button>
  );
}

function ColFilterCell({ filterKey, listType, params, updateParams, statusNames, containerNumbers, orderTypeNames, paymentTypeNames, isSupplierType, className = '' }) {
  return (
    <td style={{ padding: '7px 10px', borderBottom: `1px solid ${T.line}` }} className={`staff-list-col-filter ${className}`.trim()}>
      <StaffListColFilter
        filterKey={filterKey}
        listType={listType}
        params={params}
        updateParams={updateParams}
        statusNames={statusNames}
        containerNumbers={containerNumbers}
        orderTypeNames={orderTypeNames}
        paymentTypeNames={paymentTypeNames}
        isSupplierType={isSupplierType}
      />
    </td>
  );
}

/* -------------------------------------------------------------- component */

export default function StaffListView({
  orders = sampleOrders,
  title = 'Orders',
  subtitle = 'Customer Orders',
  noun = 'orders',
  newLabel = 'New order',
  listType = 'order',
  role,
  isManage = false,
  isSupplierInvoice = false,
  typeOptions = [],
  typeValue,
  onTypeChange,
  filterType,
  isSupplierType = false,
  isReorderMode = false,
  reorderDate,
  onReorderDateChange,
  peopleLabel = 'Customer',
  peopleOptions = [],
  people,
  onPeopleChange,
  showPeople = true,
  salesOptions = [],
  salesPerson,
  onSalesChange,
  showSales = true,
  scope = { invoiced: false, archived: false, vas: false, actionable: false, cancelled: false },
  onToggleScope,
  showScope = { invoiced: true, archived: true, vas: true, actionable: true, cancelled: false },
  vasTypes = [],
  onVasTypesChange,
  filterCount = 0,
  onRefresh,
  onResetFilters,
  onClearColFilters,
  layoutUserId,
  shownCount,
  totalCount,
  page = 0,
  pageSize = 10,
  pageSizeOptions = [5, 10, 25],
  pageCount = 1,
  onPageChange,
  onPageSizeChange,
  sortKey,
  sortDir,
  onSort,
  colParams = {},
  updateColParam,
  statusNames = [],
  containerNumbers = [],
  orderTypeNames = [],
  paymentTypeNames = [],
  headerActions = [],
  importSlot,
  exportSlot,
  showImport = false,
  showExport = false,
  showCreate = true,
  initialFiltersOpen = false,
  initialColumnFiltersOpen = true,
  hiddenColumns = DEFAULT_HIDDEN_COLUMNS,
  onBulkAction,
  onNewOrder,
  loading = false,
}) {
  const form = useForm();
  const [selected, setSelected] = useState({});
  const [importOpen, setImportOpen] = useState(false);
  const importMenuRef = useRef(null);
  const colPickerRef = useRef(null);
  const tableRef = useRef(null);
  // Container Actions: start at the Orders gutter, then shrink to the busiest row.
  // Starting at 48 caused a one-frame widen when icons mounted; shrinking is quieter.
  const [actionsColWidth, setActionsColWidth] = useState(STAFF_LIST_ACTIONS_COL_WIDTH);
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(initialFiltersOpen);
  const [colFiltersOpen, setColFiltersOpen] = useState(() =>
    staffListColFiltersOpen(readStaffListLayout(undefined, listType, layoutUserId), initialColumnFiltersOpen));
  const [cols, setCols] = useState(() => {
    const defaults = staffListDefaultCols(COLUMNS.map((c) => c.key), hiddenColumns);
    return {
      ...mergeStaffListCols(defaults, readStaffListLayout(undefined, listType, layoutUserId)?.cols),
      customerName: isSupplierType,
    };
  });
  const layoutScopeRef = useRef(`${listType}:${layoutUserId || ''}`);

  useEffect(() => {
    setCols((s) => ({ ...s, customerName: isSupplierType }));
    setSelected({});
  }, [isSupplierType, typeValue]);

  useEffect(() => {
    const scope = `${listType}:${layoutUserId || ''}`;
    if (layoutScopeRef.current !== scope) {
      layoutScopeRef.current = scope;
      const stored = readStaffListLayout(undefined, listType, layoutUserId);
      const defaults = staffListDefaultCols(COLUMNS.map((c) => c.key), hiddenColumns);
      setCols({
        ...mergeStaffListCols(defaults, stored?.cols),
        customerName: isSupplierType,
      });
      setColFiltersOpen(staffListColFiltersOpen(stored, initialColumnFiltersOpen));
      return;
    }
    writeStaffListLayout({ cols, colFiltersOpen }, undefined, listType, layoutUserId);
  }, [cols, colFiltersOpen, listType, layoutUserId, isSupplierType, hiddenColumns, initialColumnFiltersOpen]);

  const toggleScope = k => onToggleScope && onToggleScope(k);
  const doSort = key => {
    // Invoice-only columns (due date, shipped date, CTN) sort through staffListSortParamKey
    // and are not part of the order-list sort map.
    if (staffListSortParamKey(key, { listType, isSupplierType })) {
      onSort && onSort(key);
    }
  };
  const arrow = key => {
    // PGSS-5546: invoice sort writes invoice_date / invoice_no / … — match those, not order keys.
    const param = staffListSortParamKey(key, { listType, isSupplierType }) || STAFF_LIST_SORT_KEYS[key];
    const matches = key === 'customer' && !isContainerList
      ? (sortKey === 'name' || sortKey === 'supplier_name')
      : sortKey === param;
    if (!param || !matches) return '';
    // Days in status sorts by the status change time, which runs the other way: oldest change = most days.
    const ascending = key === 'statusDays' ? sortDir !== 'ASC' : sortDir === 'ASC';
    return ascending ? '  ↑' : '  ↓';
  };

  const rows = orders;
  const isContainerList = staffListIsContainer(listType);
  // The first column carries the pin, the primary link and the record sort: Order no, or
  // Container Number on the container shipments list.
  const primaryKey = isContainerList ? 'container' : 'orderNo';
  const pickerCols = COLUMNS.filter((c) => {
    if (isContainerList) return staffListShowsOrderOnlyColumn(listType, c.key);
    if (STAFF_LIST_CONTAINER_ONLY_COLUMN_KEYS.includes(c.key)) return false;
    if (c.key === 'customerName') return isSupplierType;
    if (c.key === 'container') return staffListShowsContainer(listType);
    if (c.key === 'weight') return staffListShowsWeight(listType);
    if (c.key === 'amount') return staffListShowsAmount(listType);
    if (c.key === 'tax') return staffListShowsTax(listType);
    if (c.key === 'paid') return staffListShowsPaid(listType);
    if (c.key === 'due') return staffListShowsDue(listType);
    if (c.key === 'paymentType') return staffListShowsPaymentType(listType, isSupplierInvoice);
    if (c.key === 'dueDate') return staffListShowsDueDate(listType);
    if (c.key === 'shippedDate') return staffListShowsShippedDate(listType, isSupplierInvoice);
    if (c.key === 'ctn') return staffListShowsCtn(listType, isSupplierInvoice);
    if (c.key === 'quoteCounts') return staffListShowsQuoteCounts(listType);
    if (c.key === 'statusDays') return staffListShowsStatusDays(listType);
    if (!staffListShowsOrderOnlyColumn(listType, c.key)) return false;
    return true;
  });
  // Order / Quote / RFQ: checkbox + Order no stay on the left and Actions on the right while the
  // table scrolls sideways, and the pinned Order no is narrower. Other lists (Invoice) get no pin
  // classes and keep their column widths, so they render as before.
  const pinned = staffListPinsColumns(listType, typeValue);
  const pin = (key) => (pinned ? STAFF_LIST_PIN_CLASS[key] : '');
  const resolveCol = (c) => staffListPinnedColumn(staffListResolvedColumn(c, listType), pinned);
  const visibleCols = pickerCols
    .filter((c) => cols[c.key])
    .map(resolveCol);
  const showContainer = staffListShowsContainer(listType) && cols.container;
  const showShipmentDate = isContainerList && cols.shipmentDate;
  const showCapacity = isContainerList && cols.capacity;
  const showNoOfOrders = isContainerList && cols.noOfOrders;
  const showLocation = isContainerList && cols.location;
  const showContainerCtn = isContainerList && cols.ctn;
  const showOrderDate = !isContainerList && cols.orderDate;
  const showWeight = staffListShowsWeight(listType) && cols.weight;
  const showAmount = staffListShowsAmount(listType) && cols.amount;
  const showTax = staffListShowsTax(listType) && cols.tax;
  const showPaid = staffListShowsPaid(listType) && cols.paid;
  const showDue = staffListShowsDue(listType) && cols.due;
  const showPaymentType = staffListShowsPaymentType(listType, isSupplierInvoice) && cols.paymentType;
  const showDueDate = staffListShowsDueDate(listType) && cols.dueDate;
  const showShippedDate = staffListShowsShippedDate(listType, isSupplierInvoice) && cols.shippedDate;
  const showCtn = staffListShowsCtn(listType, isSupplierInvoice) && cols.ctn;
  const showQuoteCounts = staffListShowsQuoteCounts(listType) && cols.quoteCounts;
  const showStatusDays = staffListShowsStatusDays(listType) && cols.statusDays;
  // Must match thead / tbody / filter row or table-layout:fixed collapses (invoice overlap).
  const showOrderCol = (key) => Boolean(cols[key] && staffListShowsOrderOnlyColumn(listType, key));
  const colDef = (key) => resolveCol(COLUMNS.find((c) => c.key === key));
  const cellWidth = (key) => staffListColumnWidthStyle(colDef(key));
  const hiddenCount = pickerCols.length - visibleCols.length;
  const showRowActions = !hideStaffListRowActions(typeValue);
  const actionsWidth = isContainerList ? actionsColWidth : STAFF_LIST_ACTIONS_COL_WIDTH;
  const actionsPad = isContainerList && actionsWidth < STAFF_LIST_ACTIONS_COL_WIDTH
    ? { th: '9px 8px', td: '0 8px', filter: '7px 8px' }
    : { th: '9px 16px 9px 8px', td: '0 12px 0 8px', filter: '7px 16px 7px 8px' };
  const minWidth = staffListTableMinWidth(visibleCols, {
    includeActions: showRowActions,
    actionsWidth: showRowActions ? actionsWidth : undefined,
  });
  const selRows = rows.filter(r => selected[r.id]);
  const selCount = selRows.length;
  const allSelected = rows.length > 0 && selCount === rows.length;
  const selQty = selRows.reduce((a, r) => a + r.qty, 0);
  const colFilterCount = STAFF_LIST_COL_FILTER_PARAM_KEYS.filter((key) => {
    const value = colParams[key];
    return value != null && value !== '';
  }).length;
  const headerFilterCount = filterCount;
  const activeFilterCount = staffListResetClearsCount(filterCount, colFilterCount);
  const displayShown = shownCount ?? rows.length;
  const displayTotal = totalCount ?? orders.length;
  const importActions = headerActions.filter((action) => action.group === 'import');
  const otherActions = headerActions.filter((action) => action.group !== 'import');
  const importSections = importActions.reduce((sections, action) => {
    const name = action.section || 'Import';
    const last = sections[sections.length - 1];
    if (!last || last.name !== name) {
      sections.push({ name, items: [action] });
    } else {
      last.items.push(action);
    }
    return sections;
  }, []);

  useEffect(() => {
    if (!importOpen && !colPickerOpen) return undefined;
    const onDoc = (event) => {
      if (importOpen && !importMenuRef.current?.contains(event.target)) {
        setImportOpen(false);
      }
      if (colPickerOpen && !colPickerRef.current?.contains(event.target)) {
        setColPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [importOpen, colPickerOpen]);

  // PGSS-5655: size the pinned Actions column from the busiest row on this page so a
  // single-icon Remaining-shipments view is not stuck in a 160px frozen gutter.
  useLayoutEffect(() => {
    if (!isContainerList || !showRowActions) {
      setActionsColWidth(STAFF_LIST_ACTIONS_COL_WIDTH);
      return undefined;
    }
    const table = tableRef.current;
    if (!table) return undefined;
    const measure = () => {
      const roots = table.querySelectorAll('td.staff-list-pin-actions .staff-list-row-actions');
      if (!roots.length) return;
      let maxSlots = 1;
      roots.forEach((root) => {
        const total = [...root.querySelectorAll('.MuiIconButton-root')].filter(
          (button) => !button.dataset.staffListMore && button.dataset.staffListTypeHidden !== '1',
        ).length;
        const shown = Math.min(3, total);
        const more = total > 3 ? 1 : 0;
        maxSlots = Math.max(maxSlots, shown + more);
      });
      const next = staffListPinnedActionsColWidth(maxSlots);
      setActionsColWidth((prev) => (prev === next ? prev : next));
    };
    measure();
    // Watch action roots only: button mount/unmount and type-hidden flags.
    // Do not watch `class` — hover styles elsewhere in the table would re-measure.
    const observer = typeof MutationObserver === 'undefined'
      ? null
      : new MutationObserver(measure);
    if (observer) {
      table.querySelectorAll('td.staff-list-pin-actions .staff-list-row-actions').forEach((root) => {
        observer.observe(root, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['data-staff-list-type-hidden'],
        });
      });
    }
    return () => {
      if (observer) observer.disconnect();
    };
  }, [isContainerList, showRowActions, orders, loading, typeValue]);

  const restoreDefaultCols = () => {
    const defaults = staffListDefaultCols(COLUMNS.map((c) => c.key), hiddenColumns);
    setCols({ ...defaults, customerName: isSupplierType });
  };
  const resetFilters = () => {
    setSelected({});
    if (onResetFilters) onResetFilters();
  };
  const toggleAll = () => {
    if (allSelected) return setSelected({});
    setSelected(rows.reduce((a, r) => ({ ...a, [r.id]: true }), {}));
  };

  const summaryChips = [
    { label: 'Type', value: subtitle },
    showPeople && { label: peopleLabel, value: (people?.id && people.id !== 'all' && people.name) || `All ${peopleLabel.toLowerCase()}s` },
    showSales && { label: 'Sales person', value: salesPerson?.name || 'All sales persons' },
    ...[['invoiced', 'Invoiced'], ['archived', 'Archived'], ['vas', 'Only VAS'], ['actionable', 'Actionable only'], ['cancelled', 'Cancelled']]
      .filter(([k]) => scope[k]).map(([, label]) => ({ label: 'Scope', value: label })),
    ...(scope.vas && vasTypes.length ? [{ label: 'VAS type', value: vasTypeFilterLabels(vasTypes) }] : []),
    ...(colFilterCount ? [{ label: 'Columns', value: colFilterCount + ' active' }] : []),
  ].filter(Boolean);

  const th = (col, extra) => ({ ...thBase, ...staffListColumnWidthStyle(col), textAlign: col.align, ...extra });
  // `extra` goes last so a caller can lift the nowrap default — PGSS-5556 needs that for the
  // Applied Amount header, which is too long for its column.
  const sortableTh = (col, extra) => ({
    ...th(col),
    cursor: (isContainerList ? staffListSortParamKey(col.key, { listType }) : STAFF_LIST_SORT_KEYS[col.key]) ? 'pointer' : 'default',
    whiteSpace: 'nowrap',
    ...extra,
  });

  return (
    <FormProvider {...form}>
    <div className="staff-list-page" data-testid={STAFF_LIST_TEST_IDS.page} style={{ background: T.page, fontFamily: T.sans, color: T.ink,
      WebkitFontSmoothing: 'antialiased' }}>

      {/* header */}
      <header style={{ background: '#fff', borderBottom: `1px solid ${T.line}`, padding: '18px 28px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: '-0.01em' }}>{title}</h1>
            <p style={{ margin: 0, fontSize: 12.5, color: T.muted }}>
              {subtitle} · <span style={{ fontVariantNumeric: 'tabular-nums' }}>{Number(displayShown).toLocaleString()}</span> of{' '}
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{Number(displayTotal).toLocaleString()}</span> {noun}
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Button active={filtersOpen} data-testid={STAFF_LIST_TEST_IDS.filtersToggle} onClick={() => setFiltersOpen(v => !v)}>
              <Icon d={P.filter} />
              Filters
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                minWidth: 17, height: 17, padding: '0 4px', background: T.blue, borderRadius: 9,
                fontSize: 10.5, fontWeight: 600, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
                {headerFilterCount}
              </span>
              <Icon d={P.chevron} size={12} width={2}
                style={{ color: T.faint, transform: filtersOpen ? 'rotate(180deg)' : 'none' }} />
            </Button>

            <div style={{ width: 1, height: 22, background: '#e8ebf0', margin: '0 2px' }} />

            {importActions.length > 0 && (
              <div ref={importMenuRef} style={{ position: 'relative' }}>
                <Button active={importOpen} data-testid={STAFF_LIST_TEST_IDS.import} onClick={() => setImportOpen((open) => !open)}>
                  <Icon d={P.upload} />
                  Import
                  <Icon d={P.chevron} size={12} width={2}
                    style={{ color: T.faint, transform: importOpen ? 'rotate(180deg)' : 'none' }} />
                </Button>
                <div className="staff-list-hidden-imports">
                  {importActions.filter((action) => action.node).map((action) => (
                    <div key={action.key}>{action.node}</div>
                  ))}
                </div>
                {importOpen && (
                  <div data-testid={STAFF_LIST_TEST_IDS.importMenu} style={{ position: 'absolute', top: 40, right: 0, zIndex: 30, width: 236,
                    maxWidth: 'calc(100vw - 32px)', background: '#fff', border: `1px solid ${T.line}`,
                    borderRadius: 9, boxShadow: '0 12px 28px rgba(16,24,40,.14)', padding: 6 }}>
                    {importSections.map((section, index) => (
                      <div key={section.name}>
                        {index > 0 && <div style={{ height: 1, background: T.line2, margin: '5px 8px' }} />}
                        <div style={{ ...eyebrow, padding: '7px 10px 5px', color: T.faint }}>{section.name}</div>
                        {section.items.map((action) => (
                          <MenuItem
                            key={action.key}
                            icon={P[action.icon] || P.upload}
                            color={action.icon === 'excel' ? '#1a7f52' : T.muted}
                            title={action.title || action.label}
                            href={action.href}
                            to={action.to}
                            download={action.download}
                            onClick={() => {
                              if (action.inputId) {
                                const input = document.getElementById(action.inputId);
                                const label = input?.closest('label');
                                if (label) label.click();
                                else input?.click();
                              }
                              setImportOpen(false);
                            }}
                          >
                            {action.label}
                          </MenuItem>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {otherActions.map((action) => {
              if (action.href) {
                return (
                  <a
                    key={action.key}
                    href={action.href}
                    download={action.download}
                    title={action.title || action.label}
                    style={{ textDecoration: 'none' }}
                  >
                    <Button>
                      <Icon d={P[action.icon] || P.download} />
                      {action.label}
                    </Button>
                  </a>
                );
              }
              if (action.onClick) {
                return (
                  <Button key={action.key} data-testid={action.testId} title={action.title || action.label} onClick={action.onClick}>
                    <Icon d={P[action.icon] || P.download} />
                    {action.label}
                  </Button>
                );
              }
              return (
                <div key={action.key} style={{ position: 'relative' }} title={action.title || action.label} data-testid={action.key === 'export' ? STAFF_LIST_TEST_IDS.export : undefined}>
                  <Button>
                    <Icon d={P[action.icon] || P.download} />
                    {action.label}
                  </Button>
                  <div className="staff-list-file-btn-hit">{action.node}</div>
                </div>
              );
            })}

            {showImport && importSlot && (
              <div style={{ position: 'relative' }}>
                <Button><Icon d={P.upload} />Import</Button>
                {importSlot}
              </div>
            )}

            {showExport && exportSlot && (
              <div style={{ position: 'relative' }}>
                <Button>
                  <Icon d={P.download} />Export
                </Button>
                {exportSlot}
              </div>
            )}

            {showCreate && (
              <MuiButton
                variant="contained"
                color="primary"
                onClick={onNewOrder}
                data-testid={STAFF_LIST_TEST_IDS.newItem}
                startIcon={<Icon d={P.plus} width={2} />}
                sx={{
                  height: 34,
                  borderRadius: '7px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: 13,
                  boxShadow: 'none',
                  px: 1.75,
                  '&:hover': { boxShadow: 'none' },
                }}
              >
                {newLabel}
              </MuiButton>
            )}
          </div>
        </div>
      </header>

      {/* filter chip strip (collapsed) */}
      {!filtersOpen && (
        <section style={{ background: '#fff', borderBottom: `1px solid ${T.line}`, padding: '9px 28px',
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={eyebrow}>Filtered by</span>
          {summaryChips.map((chip, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 9px',
              background: '#f6f8fb', border: `1px solid ${T.line}`, borderRadius: 20, fontSize: 12, color: T.ink2 }}>
              <span style={{ color: T.faint }}>{chip.label}</span>
              <span style={{ fontWeight: 500 }}>{chip.value}</span>
            </span>
          ))}
          <Button variant="ghost" data-testid={STAFF_LIST_TEST_IDS.resetFilters} style={{ marginLeft: 'auto', height: 26, padding: '0 8px',
            fontSize: 12.5, color: T.blue, fontWeight: 500 }} onClick={resetFilters}>Reset filters</Button>
        </section>
      )}

      {/* filter panel (expanded) */}
      {filtersOpen && (
        <section data-testid={STAFF_LIST_TEST_IDS.filters} style={{ background: '#fff', borderBottom: `1px solid ${T.line}`, padding: '14px 28px',
          display: 'flex', alignItems: 'flex-end', gap: 20, flexWrap: 'wrap' }}>
          {/* Uncontrolled Autocomplete (defaultValue). Remount after URL hydrate so Type/people/sales show the restored selection. */}
          {typeOptions.length > 0 && (
            <TypeField
              key={typeValue || 'type'}
              options={typeOptions}
              value={typeValue}
              onChange={(next) => onTypeChange && onTypeChange(next?.value)}
            />
          )}
          {showPeople && (
            <PeopleField
              key={`people-${isSupplierType ? 's' : 'c'}-${people?.id || 'all'}`}
              label={peopleLabel}
              placeholder={`All ${peopleLabel.toLowerCase()}s`}
              inputValues={peopleOptions}
              defaultValue={people?.id && people.id !== 'all' ? people : null}
              onChange={onPeopleChange}
              testId={STAFF_LIST_TEST_IDS.people}
            />
          )}
          {showSales && (
            <PeopleField
              key={`sales-${salesPerson?.id || 'all'}`}
              label="Sales person"
              placeholder="All sales persons"
              inputValues={salesOptions}
              defaultValue={salesPerson || null}
              onChange={onSalesChange}
              testId={STAFF_LIST_TEST_IDS.sales}
            />
          )}
          {isReorderMode && (
            <ReorderDateField value={reorderDate} onChange={onReorderDateChange} />
          )}

          {Object.values(showScope).some(Boolean) && (
            <div style={{ width: 1, alignSelf: 'stretch', background: '#e8ebf0', margin: '0 2px' }} />
          )}

          {Object.values(showScope).some(Boolean) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={eyebrow}>Scope</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              {SCOPE_CHIP_KEYS.filter((k) => showScope[k]).map((k) => (
                  <Button
                    key={k}
                    active={scope[k]}
                    data-testid={SCOPE_TEST_IDS[k]}
                    onClick={() => toggleScope(k)}
                    style={{ fontSize: 12.5 }}
                  >
                    {staffListScopeLabel(k, listType)}
                  </Button>
                ))}
              {shouldShowOrderVasTypes(scope.vas, listType) && (
                <select
                  aria-label="VAS type"
                  data-testid={STAFF_LIST_TEST_IDS.vasType}
                  value={vasTypes[0] || ''}
                  onChange={(event) => onVasTypesChange && onVasTypesChange(event.target.value ? [event.target.value] : [])}
                  style={selectStyle(28)}
                >
                  <option value="">All types</option>
                  {VAS_TYPE_CHOICES.map((choice) => (
                    <option key={choice.value} value={choice.value}>{choice.label}</option>
                  ))}
                </select>
              )}
            </div>
          </div>
          )}

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 1 }}>
            <span data-testid={STAFF_LIST_TEST_IDS.filtersActive} style={{ fontSize: 12, color: T.label, whiteSpace: 'nowrap' }}>
              <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: T.ink2 }}>{activeFilterCount}</span>
              {activeFilterCount === 1 ? ' filter active' : ' filters active'}
            </span>
            <Button style={{ fontSize: 12.5 }} onClick={resetFilters}>Reset filters</Button>
          </div>
        </section>
      )}

      <main style={{ padding: '20px 28px 32px' }}>
        <div style={{ background: '#fff', border: `1px solid ${T.line}`, borderRadius: 10,
          boxShadow: '0 1px 2px rgba(16,24,40,.04)' }}>

          {/* table toolbar */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: '10px 16px', flexWrap: 'wrap', padding: '11px 16px', borderBottom: `1px solid ${T.line2}`,
            background: selCount ? T.rowSelected : '#fff', borderRadius: '10px 10px 0 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px 12px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>
                {selCount ? `${selCount} selected` : `All ${noun}`}
              </span>
              {selCount > 0 && (
                <>
                  {!isContainerList && (
                    <span style={{ fontSize: 12.5, color: T.muted, fontVariantNumeric: 'tabular-nums' }}>
                      {selQty === 1 ? '1 item' : `${selQty} items`} ·{' '}
                      <span style={{ fontFamily: T.mono, fontWeight: 500, color: T.ink }}>
                        {money(selRows.reduce((a, r) => a + r.amount, 0))}
                      </span>
                    </span>
                  )}
                  <div data-testid={STAFF_LIST_TEST_IDS.bulk} className="staff-list-bulk" style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                    <OrdersToolBar
                      toggleAllRowsSelected={() => setSelected({})}
                      selectedRowIds={selected}
                      selectedFlatRows={selRows.filter((row) => row.original).map((row) => ({ original: row.original }))}
                      toolbarParam={{
                        options: filterType || { value: typeValue },
                        stage: listType,
                      }}
                    />
                    <Button variant="ghost" size="sm" style={{ padding: '0 8px', fontSize: 12.5, textDecoration: 'underline' }}
                      onClick={() => setSelected({})}>Clear</Button>
                  </div>
                </>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
              {/* PGSS-5449: the list refetches only when the URL changes, so with no filters
                  applied there was nothing that could re-request the same URL. Lives in the table
                  toolbar rather than the filter bar because it must be reachable when no filters
                  are set -- which is the exact case reported. */}
              {onRefresh && (
                <Button size="sm" data-testid={STAFF_LIST_TEST_IDS.refresh} disabled={loading}
                  title="Refresh records" onClick={onRefresh}
                  style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'default' : 'pointer' }}>
                  <Icon d={P.refresh} size={14} />
                  {loading ? 'Refreshing' : 'Refresh'}
                </Button>
              )}
              <div ref={colPickerRef} style={{ position: 'relative' }}>
                <Button size="sm" active={hiddenCount > 0} data-testid={STAFF_LIST_TEST_IDS.columns} onClick={() => setColPickerOpen(v => !v)}>
                  <Icon d={P.columns} size={14} />
                  Columns
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    padding: '0 6px', height: 17, background: '#eef1f5', borderRadius: 9, fontSize: 10.5,
                    fontWeight: 600, color: T.muted, fontVariantNumeric: 'tabular-nums' }}>
                    {visibleCols.length} of {pickerCols.length}
                  </span>
                </Button>
                {colPickerOpen && (
                  <div data-testid={STAFF_LIST_TEST_IDS.columnPicker} style={{ position: 'absolute', top: 36, left: 0, zIndex: 40, width: 250,
                    maxWidth: 'calc(100vw - 32px)', background: '#fff', border: `1px solid ${T.line}`,
                    borderRadius: 9, boxShadow: '0 12px 28px rgba(16,24,40,.14)', padding: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '4px 6px 8px' }}>
                      <span style={{ ...eyebrow, color: T.faint }}>Visible columns</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <button type="button" onClick={() => setCols(pickerCols.reduce((a, c) => ({ ...a, [c.key]: true }), {}))}
                          style={{ background: 'transparent', border: 0, padding: 0, fontSize: 11.5,
                            fontWeight: 500, color: T.blue, cursor: 'pointer', fontFamily: 'inherit' }}>Show all</button>
                        <button type="button" data-testid={STAFF_LIST_TEST_IDS.restoreColumns} onClick={restoreDefaultCols}
                          style={{ background: 'transparent', border: 0, padding: 0, fontSize: 11.5,
                            fontWeight: 500, color: T.blue, cursor: 'pointer', fontFamily: 'inherit' }}>Restore defaults</button>
                      </div>
                    </div>
                    <div style={{ maxHeight: 290, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {pickerCols.map(c => (
                        <label key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 9,
                          padding: '6px 7px', borderRadius: 6, fontSize: 13, color: T.ink2, cursor: 'pointer' }}>
                          <input type="checkbox" checked={!!cols[c.key]} style={checkbox}
                            onChange={() => setCols((s) => {
                              const next = { ...s, [c.key]: !s[c.key] };
                              if (!next[c.key] && !pickerCols.some((col) => col.key !== c.key && next[col.key])) {
                                return s;
                              }
                              return next;
                            })} />
                          {c.key === 'customer' && isContainerList
                            ? 'Name'
                            : c.key === 'container' && isContainerList
                              ? 'Container Number'
                            : c.key === 'customer' && isSupplierType
                            ? 'Supplier'
                            : c.key === 'customerName'
                              ? 'Customer'
                              : c.key === 'orderNo'
                                ? staffListRecordNoLabel(listType)
                                : c.key === 'eta'
                                  ? staffListEtaLabel(listType)
                                  : c.key === 'orderDate'
                                    ? staffListDateLabel(listType)
                                    : c.label}
                        </label>
                      ))}
                    </div>
                    <div style={{ borderTop: `1px solid ${T.line2}`, marginTop: 6, padding: '8px 7px 2px',
                      fontSize: 11.5, color: T.label }}>
                      Hiding columns is the fastest way to fit the table without scrolling.
                    </div>
                  </div>
                )}
              </div>

              <Button size="sm" data-testid={STAFF_LIST_TEST_IDS.colFilters} onClick={() => setColFiltersOpen(v => !v)}>
                <Icon d={P.filterSm} size={14} />
                {colFiltersOpen ? 'Hide column filters' : 'Column filters'}
                {colFilterCount > 0 && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    minWidth: 17, height: 17, padding: '0 4px', background: T.blue, borderRadius: 9,
                    fontSize: 10.5, fontWeight: 600, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
                    {colFilterCount}
                  </span>
                )}
              </Button>
              {colFilterCount > 0 && (
                <Button size="sm" data-testid={STAFF_LIST_TEST_IDS.clearColFilters}
                  onClick={() => onClearColFilters && onClearColFilters()}>
                  Clear column filters
                </Button>
              )}
            </div>
          </div>

          {/* table */}
          <div className="staff-list-table-scroll">
            <table
              ref={tableRef}
              data-testid={STAFF_LIST_TEST_IDS.table}
              className={`staff-list-grid${showRowActions ? '' : ' staff-list-grid--no-actions'}${pinned ? ' staff-list-grid--pinned' : ''}`}
              style={{
                width: '100%',
                minWidth,
                tableLayout: 'fixed',
                borderCollapse: 'collapse',
                fontSize: 13,
                '--staff-list-pin-no-left': `${STAFF_LIST_CHECKBOX_COL_WIDTH}px`,
                '--staff-list-actions-width': `${actionsWidth}px`,
              }}
            >
              <thead>
                <tr style={{ background: '#fafbfc' }}>
                  <th className={pin('check')} style={{ ...thBase, width: 44, padding: '9px 0 9px 16px' }}>
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} style={checkbox}
                      aria-label="Select all orders" />
                  </th>
                  {cols[primaryKey] && <th className={pin('orderNo')} style={sortableTh(colDef(primaryKey), { padding: '9px 14px 9px 4px' })}
                    onClick={() => doSort(primaryKey)}>{isContainerList ? 'Container Number' : staffListRecordNoLabel(listType)}{arrow(primaryKey)}</th>}
                  {showContainerCtn && <th style={sortableTh(colDef('ctn'))} onClick={() => doSort('ctn')}>CTN{arrow('ctn')}</th>}
                  {cols.customer && <th style={sortableTh(colDef('customer'))} onClick={() => doSort('customer')}>{isContainerList ? 'Name' : isSupplierType ? 'Supplier' : 'Customer'}{arrow('customer')}</th>}
                  {isSupplierType && cols.customerName && <th style={sortableTh(colDef('customerName'))}>Customer</th>}
                  {showOrderDate && <th style={sortableTh(colDef('orderDate'))} onClick={() => doSort('orderDate')}>{staffListDateLabel(listType)}{arrow('orderDate')}</th>}
                  {showDueDate && <th style={sortableTh(colDef('dueDate'))} onClick={() => doSort('dueDate')}>Due date{arrow('dueDate')}</th>}
                  {showShippedDate && <th style={sortableTh(colDef('shippedDate'))} onClick={() => doSort('shippedDate')}>Shipped date{arrow('shippedDate')}</th>}
                  {showOrderCol('ref') && <th style={sortableTh(colDef('ref'))} onClick={() => doSort('ref')}>Reference no{arrow('ref')}</th>}
                  {showOrderCol('decedent') && <th style={sortableTh(colDef('decedent'))} onClick={() => doSort('decedent')}>Decedent{arrow('decedent')}</th>}
                  {showOrderCol('comments') && <th style={th(colDef('comments'), { padding: '9px 10px' })}>Notes</th>}
                  {showShipmentDate && <th style={sortableTh(colDef('shipmentDate'))} onClick={() => doSort('shipmentDate')}>Shipment Date{arrow('shipmentDate')}</th>}
                  {showOrderCol('eta') && <th style={sortableTh(colDef('eta'))} onClick={() => doSort('eta')}>{staffListEtaLabel(listType)}{arrow('eta')}</th>}
                  {showWeight && (isContainerList
                    // PGSS-5655: a container's weight is computed from its orders, not stored, so it cannot sort.
                    ? <th style={th(colDef('weight'))}>{displayName?.weight}</th>
                    : <th style={sortableTh(colDef('weight'))} onClick={() => doSort('weight')}>{displayName?.weight}{arrow('weight')}</th>)}
                  {showCapacity && <th style={th(colDef('capacity'))}>Capacity</th>}
                  {showNoOfOrders && <th style={th(colDef('noOfOrders'))}>No of Orders</th>}
                  {showOrderCol('qty') && <th style={sortableTh(colDef('qty'), { padding: '9px 10px' })} onClick={() => doSort('qty')}>Qty{arrow('qty')}</th>}
                  {showAmount && <th style={sortableTh(colDef('amount'))} onClick={() => doSort('amount')}>{listType === 'invoice' ? 'Invoice amount' : 'Amount'}{arrow('amount')}</th>}
                  {showTax && <th style={th(colDef('tax'), STAFF_LIST_WRAPPING_HEADER)}>{staffListTaxLabel()}</th>}
                  {showPaid && <th style={sortableTh(colDef('paid'), STAFF_LIST_WRAPPING_HEADER)} onClick={() => doSort('paid')}>{staffListPaidLabel(listType, isSupplierInvoice)}{arrow('paid')}</th>}
                  {showDue && <th style={sortableTh(colDef('due'))} onClick={() => doSort('due')}>Due{arrow('due')}</th>}
                  {showPaymentType && <th style={th(colDef('paymentType'), STAFF_LIST_WRAPPING_HEADER)}>Payment type</th>}
                  {showOrderCol('type') && <th style={th(colDef('type'), { padding: '9px 10px' })}>{isContainerList ? 'Order Type' : 'Type'}</th>}
                  {showContainer && <th style={th(colDef('container'), { whiteSpace: 'nowrap' })}>Container</th>}
                  {showCtn && <th style={th(colDef('ctn'))}>CTN</th>}
                  {showLocation && <th style={sortableTh(colDef('location'))} onClick={() => doSort('location')}>Location{arrow('location')}</th>}
                  {cols.status && <th style={sortableTh(colDef('status'))} onClick={() => doSort('status')}>Status{arrow('status')}</th>}
                  {showStatusDays && <th style={sortableTh(colDef('statusDays'), { whiteSpace: 'nowrap' })} onClick={() => doSort('statusDays')}>Days in status{arrow('statusDays')}</th>}
                  {showQuoteCounts && <th style={th(colDef('quoteCounts'), { whiteSpace: 'nowrap' })}>Quote Sent/Received</th>}
                  {showRowActions && <th className={pin('actions')} style={{ ...thBase, ...staffListActionsColStyle(actionsWidth), padding: actionsPad.th, textAlign: 'left' }}>Actions</th>}
                </tr>
                {colFiltersOpen && (
                  <tr style={{ background: '#fff' }}>
                    <td className={pin('check')} style={{ padding: '7px 0 7px 16px', borderBottom: `1px solid ${T.line}` }} />
                    {cols[primaryKey] && <ColFilterCell className={pin('orderNo')} filterKey={primaryKey} listType={listType} params={colParams} updateParams={updateColParam} isSupplierType={isSupplierType} />}
                    {showContainerCtn && <ColFilterCell filterKey="ctn" listType={listType} params={colParams} updateParams={updateColParam} />}
                    {cols.customer && <ColFilterCell filterKey="customer" listType={listType} params={colParams} updateParams={updateColParam} isSupplierType={isSupplierType} />}
                    {isSupplierType && cols.customerName && <ColFilterCell filterKey="customerName" listType={listType} params={colParams} updateParams={updateColParam} />}
                    {showOrderDate && <ColFilterCell filterKey="orderDate" listType={listType} params={colParams} updateParams={updateColParam} />}
                    {showDueDate && <ColFilterCell filterKey="dueDate" listType={listType} params={colParams} updateParams={updateColParam} />}
                    {showShippedDate && <ColFilterCell filterKey="shippedDate" listType={listType} params={colParams} updateParams={updateColParam} />}
                    {showOrderCol('ref') && <ColFilterCell filterKey="ref" listType={listType} params={colParams} updateParams={updateColParam} />}
                    {showOrderCol('decedent') && <ColFilterCell filterKey="decedent" listType={listType} params={colParams} updateParams={updateColParam} />}
                    {showOrderCol('comments') && <ColFilterCell filterKey="comments" listType={listType} params={colParams} updateParams={updateColParam} />}
                    {showShipmentDate && <ColFilterCell filterKey="shipmentDate" listType={listType} params={colParams} updateParams={updateColParam} />}
                    {showOrderCol('eta') && <ColFilterCell filterKey="eta" listType={listType} params={colParams} updateParams={updateColParam} />}
                    {showWeight && <ColFilterCell filterKey="weight" listType={listType} params={colParams} updateParams={updateColParam} />}
                    {/* Capacity is a progress bar and No of Orders had no filter on the legacy list. */}
                    {showCapacity && <td style={{ padding: '7px 10px', borderBottom: `1px solid ${T.line}` }} />}
                    {showNoOfOrders && <td style={{ padding: '7px 10px', borderBottom: `1px solid ${T.line}` }} />}
                    {showOrderCol('qty') && <ColFilterCell filterKey="qty" listType={listType} params={colParams} updateParams={updateColParam} />}
                    {showAmount && <ColFilterCell filterKey="amount" listType={listType} params={colParams} updateParams={updateColParam} />}
                    {showTax && <td style={{ padding: '7px 10px', borderBottom: `1px solid ${T.line}` }} />}
                    {showPaid && <ColFilterCell filterKey="paid" listType={listType} params={colParams} updateParams={updateColParam} />}
                    {showDue && <ColFilterCell filterKey="due" listType={listType} params={colParams} updateParams={updateColParam} />}
                    {showPaymentType && <ColFilterCell filterKey="paymentType" listType={listType} params={colParams} updateParams={updateColParam} paymentTypeNames={paymentTypeNames} />}
                    {showOrderCol('type') && <ColFilterCell filterKey="type" listType={listType} params={colParams} updateParams={updateColParam} orderTypeNames={orderTypeNames} />}
                    {showContainer && <ColFilterCell filterKey="container" listType={listType} params={colParams} updateParams={updateColParam} containerNumbers={containerNumbers} />}
                    {showCtn && <ColFilterCell filterKey="ctn" listType={listType} params={colParams} updateParams={updateColParam} />}
                    {showLocation && <ColFilterCell filterKey="location" listType={listType} params={colParams} updateParams={updateColParam} />}
                    {cols.status && <ColFilterCell filterKey="status" listType={listType} params={colParams} updateParams={updateColParam} statusNames={statusNames} />}
                    {showStatusDays && <td style={{ padding: '7px 10px', borderBottom: `1px solid ${T.line}` }} />}
                    {/* Quote Sent/Received carries showFilter: false on the old list, so no filter here. */}
                    {showQuoteCounts && <td style={{ padding: '7px 10px', borderBottom: `1px solid ${T.line}` }} />}
                    {showRowActions && <td className={pin('actions')} style={{ padding: actionsPad.filter, borderBottom: `1px solid ${T.line}`, ...staffListActionsColStyle(actionsWidth) }} />}
                  </tr>
                )}
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td
                      className="staff-list-table-loading-cell"
                      colSpan={1 + visibleCols.length + (showRowActions ? 1 : 0)}
                    >
                      <div className="staff-list-table-loading" data-testid={STAFF_LIST_TEST_IDS.loader} aria-busy="true">
                        <Loader style={{ height: '30vh', width: '100%' }} />
                      </div>
                    </td>
                  </tr>
                )}
                {!loading && rows.map(row => {
                  const st = staffStatusTone(row.status);
                  const checked = !!selected[row.id];
                  return (
                    <React.Fragment key={row.recordId || row.id}>
                      <tr style={{ background: checked ? T.rowSelected : '#fff', borderBottom: `1px solid ${T.line3}` }}>
                        <td className={pin('check')} style={{ padding: '0 0 0 16px', verticalAlign: 'middle' }}>
                          <input type="checkbox" checked={checked} style={{ ...checkbox, display: 'block' }}
                            aria-label={`Select order ${row.id}`}
                            onChange={() => setSelected(s => ({ ...s, [row.id]: !s[row.id] }))} />
                        </td>
                        {cols[primaryKey] && (
                          // PGSS-5655: a long container number wraps inside its cell (staffList.css) rather than
                          // running into the CTN column or being cut to "…".
                          <td
                            style={{ padding: '9px 14px 9px 4px', verticalAlign: 'middle', ...cellWidth(primaryKey) }}
                            className={`staff-list-order-no${isContainerList ? ' staff-list-container-no' : ''} ${pin('orderNo')}`.trim()}>
                            {row.original && staffListShowsInternalNotesIcon(listType, row.original) ? (
                              // PGSS-5759: only rows with internal notes get the flex wrapper, so others lay out as before.
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <OrderViewAction
                                  row={{ original: row.original }}
                                  cell={{}}
                                  stage={listType}
                                  role={role}
                                />
                                <MuiTooltip title={STAFF_LIST_INTERNAL_NOTES_LABEL}>
                                  <span role="img" aria-label={STAFF_LIST_INTERNAL_NOTES_LABEL}
                                    data-testid="staff-list-internal-notes-icon"
                                    style={{ display: 'inline-flex', color: T.internalNote, flex: 'none' }}>
                                    <Icon d={P.internalNote} size={14} />
                                  </span>
                                </MuiTooltip>
                              </div>
                            ) : row.original ? (
                              <OrderViewAction
                                row={{ original: row.original }}
                                cell={{}}
                                stage={listType}
                                role={role}
                              />
                            ) : row.id}
                          </td>
                        )}
                        {showContainerCtn && (
                          <td title={row.ctn} style={{ padding: '9px 14px', verticalAlign: 'middle', color: T.ink2, ...clip, ...cellWidth('ctn') }}>
                            {row.ctn}
                          </td>
                        )}
                        {cols.customer && <td title={row.customer} style={{ padding: '9px 14px', verticalAlign: 'middle', color: T.ink2, fontWeight: 500, ...clip, ...cellWidth('customer') }}>{row.customer}</td>}
                        {isSupplierType && cols.customerName && <td title={row.customerName} style={{ padding: '9px 14px', verticalAlign: 'middle', color: T.ink2, fontWeight: 500, ...clip, ...cellWidth('customerName') }}>{row.customerName}</td>}
                        {showOrderDate && <td style={{ padding: '9px 14px', verticalAlign: 'middle', color: T.muted, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', ...cellWidth('orderDate') }}>{row.orderDate}</td>}
                        {showDueDate && <td style={{ padding: '9px 14px', verticalAlign: 'middle', color: T.muted, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', ...cellWidth('dueDate') }}>{row.dueDate}</td>}
                        {showShippedDate && <td style={{ padding: '9px 14px', verticalAlign: 'middle', color: T.muted, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', ...cellWidth('shippedDate') }}>{row.shippedDate}</td>}
                        {showOrderCol('ref') && <td title={row.ref} style={{ padding: '9px 14px', verticalAlign: 'middle', color: T.ink2, ...clip, ...cellWidth('ref') }}>{row.ref}</td>}
                        {showOrderCol('decedent') && <td title={row.decedent} style={{ padding: '9px 14px', verticalAlign: 'middle', color: T.muted, ...clip, ...cellWidth('decedent') }}>{row.decedent}</td>}
                        {showOrderCol('comments') && (
                          <td title={row.notes || 'Notes'} style={{ padding: '9px 10px', verticalAlign: 'middle', color: row.notes ? T.ink2 : '#c2cad4', ...clip, ...cellWidth('comments') }}>
                            {row.notes ? row.notes : <Icon d={P.comment} size={16} width={1.7} />}
                          </td>
                        )}
                        {showShipmentDate && <td style={{ padding: '9px 14px', verticalAlign: 'middle', color: T.ink2, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', ...cellWidth('shipmentDate') }}>{row.shipmentDate}</td>}
                        {showOrderCol('eta') && <td style={{ padding: '9px 14px', verticalAlign: 'middle', color: T.ink2, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', ...cellWidth('eta') }}>{row.eta}</td>}
                        {showWeight && <td style={{ padding: '9px 14px', verticalAlign: 'middle', textAlign: 'right', color: T.ink2, fontFamily: T.mono, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', ...cellWidth('weight') }}>{row.weight ? formatWeight(row.weight) : '-'}</td>}
                        {showCapacity && (
                          <td style={{ padding: '9px 14px', verticalAlign: 'middle', ...cellWidth('capacity') }}>
                            <ProgressCell row={{ original: row.original }} />
                          </td>
                        )}
                        {showNoOfOrders && <td style={{ padding: '9px 14px', verticalAlign: 'middle', color: T.ink2, fontVariantNumeric: 'tabular-nums', ...cellWidth('noOfOrders') }}>{row.noOfOrders}</td>}
                        {showOrderCol('qty') && <td style={{ padding: '9px 10px', verticalAlign: 'middle', textAlign: 'right', color: T.ink2, fontFamily: T.mono, fontVariantNumeric: 'tabular-nums', ...cellWidth('qty') }}>{row.qty}</td>}
                        {showAmount && <td style={{ padding: '9px 14px', verticalAlign: 'middle', textAlign: 'right', color: T.ink, fontFamily: T.mono, fontWeight: 500, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', ...cellWidth('amount') }}>{money(row.amount)}</td>}
                        {showTax && <td style={{ padding: '9px 14px', verticalAlign: 'middle', textAlign: 'right', color: T.ink, fontFamily: T.mono, fontWeight: 500, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', ...cellWidth('tax') }}>{money(row.tax)}</td>}
                        {showPaid && <td style={{ padding: '9px 14px', verticalAlign: 'middle', textAlign: 'right', color: T.ink, fontFamily: T.mono, fontWeight: 500, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', ...cellWidth('paid') }}>{money(row.paid)}</td>}
                        {showDue && <td style={{ padding: '9px 14px', verticalAlign: 'middle', textAlign: 'right', color: T.ink, fontFamily: T.mono, fontWeight: 500, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', ...cellWidth('due') }}>{money(row.due)}</td>}
                        {showPaymentType && <td title={row.paymentType} style={{ padding: '9px 14px', verticalAlign: 'middle', color: T.ink, ...clip, ...cellWidth('paymentType') }}>{row.paymentType}</td>}
                        {showOrderCol('type') && (
                          <td style={{ padding: '9px 10px', verticalAlign: 'middle', textAlign: 'center', overflow: 'hidden', whiteSpace: 'nowrap', ...cellWidth('type') }}>
                            <span title={isContainerList ? row.type : 'Regular order'} style={{ display: 'inline-block', minWidth: 22,
                              padding: '2px 5px', background: T.line3, border: `1px solid ${T.line}`,
                              borderRadius: 4, fontSize: 11, fontWeight: 600, color: T.muted }}>{row.type}</span>
                          </td>
                        )}
                        {showContainer && <td title={row.container} style={{ padding: '9px 14px', verticalAlign: 'middle', color: T.faint2, ...clip, ...cellWidth('container') }}>{row.container}</td>}
                        {showCtn && <td title={row.ctn} style={{ padding: '9px 14px', verticalAlign: 'middle', color: T.ink2, ...clip, ...cellWidth('ctn') }}>{row.ctn}</td>}
                        {showLocation && (
                          <td title={row.location} style={{ padding: '9px 14px', verticalAlign: 'middle', color: T.ink2, ...clip, ...cellWidth('location') }}>
                            {row.location}
                          </td>
                        )}
                        {cols.status && (
                          <td title={row.status} style={{ padding: '9px 14px', verticalAlign: 'middle', overflow: 'hidden', ...cellWidth('status') }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center',
                              gap: 6, maxWidth: '100%', minWidth: 0, overflow: 'hidden', padding: '3px 9px 3px 7px', borderRadius: 20,
                              fontSize: 11.5, fontWeight: 500, background: st.bg, color: st.fg,
                              border: `1px solid ${st.border}` }}>
                              <span style={{ flex: '0 0 auto', width: 6, height: 6, borderRadius: '50%', background: st.dot }} />
                              <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.status}</span>
                            </span>
                          </td>
                        )}
                        {showStatusDays && <td title={row.original?.status_change_time ? `Since ${formatEastern(row.original.status_change_time, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })} ET` : ''} style={{ padding: '9px 14px', verticalAlign: 'middle', textAlign: 'right', color: T.ink2, fontFamily: T.mono, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', ...cellWidth('statusDays') }}>{row.statusDays ?? '—'}</td>}
                        {showQuoteCounts && (
                          <td style={{ padding: '9px 14px', verticalAlign: 'middle', color: T.ink2, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', ...cellWidth('quoteCounts') }}>
                            {row.quoteCounts && (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                {row.quoteCounts.sentReplied}
                                {row.quoteCounts.rejected && (
                                  <span style={{ color: '#c62828', fontWeight: 700 }}>{row.quoteCounts.rejected}</span>
                                )}
                              </span>
                            )}
                          </td>
                        )}
                        {showRowActions && (
                          <td className={pin('actions')} style={{ padding: actionsPad.td, verticalAlign: 'middle', whiteSpace: 'nowrap', overflow: 'hidden', ...staffListActionsColStyle(actionsWidth) }}>
                            {row.original && (
                              <StaffListRowActions
                                row={{ original: row.original }}
                                cell={{ column: { userManage: isManage } }}
                                typeValue={typeValue}
                                listType={listType}
                                onReload={onRefresh}
                              />
                            )}
                          </td>
                        )}
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* footer */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
            padding: '11px 16px', borderTop: `1px solid ${T.line2}`, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12.5, color: T.muted2 }}>
                Showing <span style={{ fontWeight: 600, color: T.ink2, fontVariantNumeric: 'tabular-nums' }}>
                  {displayTotal ? `${page * pageSize + 1}–${page * pageSize + rows.length}` : '0'}
                </span>{' '}
                of <span style={{ fontWeight: 600, color: T.ink2, fontVariantNumeric: 'tabular-nums' }}>{Number(displayTotal).toLocaleString()}</span>
              </span>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: T.muted2 }}>
                Rows per page
                <select
                  data-testid={STAFF_LIST_TEST_IDS.rowsPerPage}
                  aria-label="rows per page"
                  value={pageSize}
                  onChange={(event) => onPageSizeChange && onPageSizeChange(Number(event.target.value))}
                  style={selectStyle(28)}
                >
                  {/* An old bookmark's size stays selectable rather than showing a size it isn't. */}
                  {(pageSizeOptions.includes(pageSize) ? pageSizeOptions : [...pageSizeOptions, pageSize].sort((a, b) => a - b))
                    .map((size) => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                </select>
              </label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <PageButton
                disabled={page <= 0}
                data-testid={STAFF_LIST_TEST_IDS.pageFirst}
                onClick={() => onPageChange && onPageChange(0)}
              >First</PageButton>
              <PageButton
                disabled={page <= 0}
                data-testid={STAFF_LIST_TEST_IDS.pagePrev}
                onClick={() => onPageChange && onPageChange(page - 1)}
              >Previous</PageButton>
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 30,
                height: 30, padding: '0 8px', background: T.blue, borderRadius: 6, fontSize: 12.5,
                fontWeight: 600, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>{page + 1}</span>
              <PageButton
                disabled={page + 1 >= pageCount}
                data-testid={STAFF_LIST_TEST_IDS.pageNext}
                onClick={() => onPageChange && onPageChange(page + 1)}
              >Next</PageButton>
              <PageButton
                disabled={page + 1 >= pageCount}
                data-testid={STAFF_LIST_TEST_IDS.pageLast}
                onClick={() => onPageChange && onPageChange(Math.max(0, pageCount - 1))}
              >Last</PageButton>
            </div>
          </div>

        </div>
      </main>
    </div>
    </FormProvider>
  );
}
